import { FC, useState, useEffect } from 'react';

import styled from 'styled-components';

import { TOUR_PAST_SEARCH, HOST_URL } from '@constants/constants';
import { callService } from '@api/caller';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { YearTabProps } from '@components/ui/search/SearchMonthDate/interface';
import SearchMonthDate from '@components/ui/search/SearchMonthDate';
import { getCookies, removeCookies } from '@libs/cookies-next/src';
import { WebUrlType } from '@api/index';

import {
  BookerSearchDrawer,
  BookerSearchViewModel,
} from './BookerSearchDrawer';

const WhereToGoSearchContainer = styled.div`
  .react-tiny-popover-container {
    width: 570px !important;
    top: -25px !important;
  }
`;

export const MonthDateWrapper = styled.div`
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    background-color: ${({ theme }) => theme.palette.white};
    padding: 0 20px;
  }
`;

export interface TourBookerError {
  location?: boolean;
}

export type TourItems = Array<{ id: number; text: string; url: string }>;
interface BookerWheretoGoProps {
  subtitle: string;
  onHrefUpdate?: (href: string) => void;
  onSearchCalled: () => void;
  onInputLocationChange?: (item: BookerSearchViewModel) => void;
  href?: string;
  type: string;
  setError: React.Dispatch<
    React.SetStateAction<Partial<TourBookerError | undefined>>
  >;
  setInitialError: React.Dispatch<
    React.SetStateAction<Partial<TourBookerError | undefined>>
  >;
  error?: TourBookerError;
  initialError?: TourBookerError;
}

export const BookerWheretoGo: FC<BookerWheretoGoProps> = ({
  subtitle,
  onHrefUpdate,
  href,
  onSearchCalled,
  type,
  onInputLocationChange,
  error,
  setError,
  setInitialError,
  initialError,
}) => {
  const [pastTourItems, setPastTourItems] = useState(
    typeof window !== 'undefined' && localStorage.getItem(TOUR_PAST_SEARCH)
      ? JSON.parse(localStorage.getItem(TOUR_PAST_SEARCH) || '')
      : []
  );
  const [searchedItemText, setSearchedItemText] = useState('');

  const clearPastTourSearch = () => {
    if (typeof window !== 'undefined')
      localStorage.removeItem(TOUR_PAST_SEARCH);
    setPastTourItems([]);
  };

  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastTourItems.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastTourItems(newItems);
    if (typeof window !== 'undefined')
      localStorage.setItem(TOUR_PAST_SEARCH, JSON.stringify(newItems));
  };

  // ===========================================
  // Handel save items to local storage
  // ===========================================

  const saveItems = async () => {
    if (!href || !type) return;

    let pastTourItems = [];
    let tourItems: TourItems = [];

    const updatedHref = href?.split?.('?')?.[0];
    const redirectUrl = getCookies().redirectUrl;

    switch (type) {
      case WebUrlType.TOUR_CITY:
      case WebUrlType.TOUR_TYPE:
      case WebUrlType.TOUR_THEME:
      case WebUrlType.TOUR_REGION:
      case WebUrlType.TOUR_COUNTRY:
      case WebUrlType.TOUR_CATEGORY:
      case WebUrlType.TOUR:
        if (
          typeof window !== 'undefined' &&
          localStorage.getItem(TOUR_PAST_SEARCH)
        ) {
          pastTourItems = JSON.parse(
            localStorage.getItem(TOUR_PAST_SEARCH) || ''
          );
        }

        tourItems = pastTourItems ? [...pastTourItems] : [];

        if (!tourItems.find((i) => i.url === updatedHref)) {
          tourItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: updatedHref,
          });
        } else {
          const target = tourItems.find((i) => i.url === updatedHref);

          const targetIndex = target ? tourItems.indexOf(target) : -1;

          tourItems.splice(targetIndex, 1);
          tourItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: updatedHref,
          });
        }

        localStorage.setItem(TOUR_PAST_SEARCH, JSON.stringify(tourItems));

        if (redirectUrl) {
          window.open(
            `${HOST_URL}${STATIC_ROUTES.tourPathPrefix}/${redirectUrl}`,
            '_self'
          );
          removeCookies('redirectUrl');
        } else {
          window.open(
            `${HOST_URL}${STATIC_ROUTES.tourPathPrefix}/${href}`,
            '_self'
          );
        }
        break;

      default:
        window.open(
          `${HOST_URL}${STATIC_ROUTES.tourPathPrefix}/${href}`,
          '_self'
        );
    }
  };

  useEffect(() => {
    if (error?.location) return;
    if (!href || !type) return;
    saveItems();
  }, [onSearchCalled]);

  useEffect(() => {
    if (!href?.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: true,
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: false,
      }));

      setError({
        ...initialError,
        location: false,
      });
    }
  }, [href, location]);

  const onDateMonthsUpdated = (info: YearTabProps[]) => {
    const [currentYear, nextYear] = info;

    const selectedItems: string[] = [];
    currentYear.months.forEach((item) => {
      if (item.checked) {
        selectedItems.push(`${currentYear.year}${item.number}`);
      }
    });

    nextYear.months.forEach((item) => {
      if (item.checked) {
        selectedItems.push(`${nextYear.year}${item.number}`);
      }
    });
    const currentHref = href?.split('?')?.[0];
    if (selectedItems.length === 0) return onHrefUpdate?.(currentHref || '');

    onHrefUpdate?.(`${currentHref}?date=${selectedItems.join('&date=')}`);
  };

  return (
    <>
      <WhereToGoSearchContainer>
        <BookerSearchDrawer
          type='Tour'
          onSearchItemTextUpdate={setSearchedItemText}
          onHrefUpdate={onHrefUpdate}
          href={href}
          placeholder={subtitle}
          responsiveTitle={subtitle}
          fetchItems={(param: string) => {
            return callService('SearchesService', 'searchesTour', {
              s: param || '',
            });
          }}
          onInputLocationChange={onInputLocationChange}
          history={pastTourItems}
          onClearClick={clearPastTourSearch}
          onRemoveItem={removeItemFromLocalStorage}
          error={error?.location}
        />
      </WhereToGoSearchContainer>
      <MonthDateWrapper>
        <SearchMonthDate
          onMonthsUpdated={onDateMonthsUpdated}
          inputComponent='setur'
        />
      </MonthDateWrapper>
    </>
  );
};
export default BookerWheretoGo;
